import { cacheHeaders, throwError } from '@adg-octopus/fyr';
import { theme } from '@flysas-tech/react-components';
import '@flysas-tech/react-components/css/global.css';
import { json, redirect } from '@remix-run/node';
import {
  Form,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  isRouteErrorResponse,
  useLocation,
  useNavigation,
  useRouteError,
  useRouteLoaderData,
} from '@remix-run/react';
import { isNil, isNotNil } from 'ramda';
import { useEffect, useState } from 'react';
// eslint-disable-next-line n/no-missing-import
import { context as tentacle } from 'virtual:octopus/tentacle-sidebar';

import { Error401, Error404, Error500, ErrorDefault, ErrorRouting } from '#components/errors.jsx';
import SASHeader from '#components/header.jsx';
import { PageLoader } from '#components/page-loader.jsx';
import Sidebar from '#components/sidebar.jsx';
import { AnalyticsProvider, gtagPageView } from '#components/use-analytics.jsx';

import tailwind from './tailwind.css?inline';

/**
 * @param {import('@adg-octopus/functions').FunctionArgs} loader loader arguments
 * @returns {import('@remix-run/node').TypedResponse<{ user: { id: string, email: string, displayName: string, accesses: Array<string> } }>} returns response
 */
export async function loader({ context, request }) {
  const [hasAccess, user] = await Promise.all([context.auth.hasAccess(tentacle, request), context.auth.getUser()]);
  const { renderOptions } = context._internal;

  if (isNil(user)) {
    const headers = cacheHeaders().noCache().create();
    return new URL(request.url).pathname.includes('/login')
      ? json({ renderOptions }, headers)
      : redirect('/login', { headers });
  }

  if (!hasAccess) {
    return throwError(
      { ...context, user },
      {
        message: `User ${user.displayName} is unauthorized to access ${request.url} route`,
        status: 401,
        code: 'UNAUTHERR',
      }
    );
  }

  return json({ user, renderOptions }, { headers: cacheHeaders({ private: true }).ttl().immutable().create() });
}

export function Layout({ children }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const error = useRouteError();
  const { renderOptions, user } = useRouteLoaderData('root') ?? error?.data ?? {};

  function handleMenuToggle() {
    setIsMenuOpen(open => !open);
  }

  useEffect(() => {
    gtagPageView(location.pathname, document.title, user?.id);
  }, [location, user]);

  const GA_ID = import.meta.env.VITE_ANALYTICS_GTAG_MEASUREMENT_ID || '';

  return (
    <AnalyticsProvider user={user}>
      <html className="h-full w-full" lang="en">
        <head>
          <meta charSet="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <script async src={`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`} />
          <script
            dangerouslySetInnerHTML={{
              __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${GA_ID}', { send_page_view: false });
            `,
            }}
          />
          <Meta />
          <Links />
        </head>
        <body className={`${theme} h-full w-full`}>
          <main className="flex bg-grey-90 min-h-full h-full w-full">
            {/* Explicit check for false to failsafe with rendering with the sidebar and header if loader is not called on re-render */}
            {user && renderOptions?.sidebar !== false && (
              <Sidebar user={user} open={isMenuOpen} onToggle={handleMenuToggle} />
            )}
            <article className="flex flex-col flex-1 overflow-auto">
              {renderOptions?.header !== false && <SASHeader loggedIn={isNotNil(user)} onMenu={handleMenuToggle} />}
              <section className="w-full h-[calc(100%-4.5rem)]">{children}</section>
            </article>
          </main>
          <ScrollRestoration />
          <Scripts />
          {/* Tailwind must be the latest stylesheet to be loaded in order to guarantee that styles applied in octopus are not overriding by something else */}
          <style suppressHydrationWarning>{tailwind}</style>
        </body>
      </html>
    </AnalyticsProvider>
  );
}

export default function App() {
  const navigation = useNavigation();

  return navigation.state === 'idle' ? <Outlet /> : <PageLoader />;
}

/**
 * Fetch access token, send email and redirect to success or error page.
 * @param {import('@adg-octopus/functions').FunctionArgs} args The action arguments.
 * @returns {Promise<ReturnType<typeof redirect>>} The response from the action.
 */
export async function action({ context, request }) {
  const formData = await request.formData();

  const message = formData.get('message');
  const errorMessage = formData.get('error-message');
  const stack = formData.get('error-stack');
  const status = formData.get('error-status');
  const statusText = formData.get('error-status-text');
  const data = formData.get('error-data');
  const correlationId = formData.get('error-correlationId');
  const pathname = formData.get('error-pathname');
  const userAgent = request.headers.get('user-agent');

  try {
    await context.client.notification.send({
      content: `${errorMessage ? `Error Message: ${errorMessage}\n\n` : ''}
      ${data ? `Data: ${data}\n\n` : ''}
      ${message ? `Message from user: ${message}\n\n` : ''}
      ${pathname ? `Pathname: ${pathname}\n\n` : ''}
      ${userAgent ? `User Agent: ${userAgent}\n\n` : ''}
      ${stack ? `Stack: ${stack}\n\n` : ''}
      ${status ? `Status: ${status}\n\n` : ''}
      ${statusText ? `Status text: ${statusText}\n\n` : ''}
      CorrelationId: ${correlationId}\n\n`,
      type: 'email',
      subject: `Error report from ${context.user?.email}`,
    });
  } catch (error) {
    return redirect('/error', { error: 'Failed to send email', success: false }, { status: error.status });
  }

  return redirect('/success', { status: 302, success: true });
}

export function ErrorBoundary() {
  const error = useRouteError();
  const location = useLocation();

  const inputs = {
    ...error,
    message: error?.message || error?.error?.message,
    stack: error?.stack || error?.error?.stack,
  };

  useEffect(() => {
    if (error instanceof Error) {
      const remotePathName = location.pathname.split('/')[1];
      localStorage.removeItem(remotePathName);
    }
  }, [error, location.pathname]);

  return (
    // add user data when auth is in place
    <Form className="h-full" method="post" id="error-form">
      <input type="hidden" name="error-message" defaultValue={inputs.message} />
      <input type="hidden" name="error-stack" defaultValue={inputs.stack} />
      <input type="hidden" name="error-status" defaultValue={inputs.status} />
      <input type="hidden" name="error-status-text" defaultValue={inputs.statusText} />
      <input type="hidden" name="error-pathname" defaultValue={location.pathname} />
      <input type="hidden" name="error-data" defaultValue={JSON.stringify(inputs.data, undefined, 2)} />
      <input type="hidden" name="error-correlationId" defaultValue={inputs.data?.correlationId} />
      {error.status === 404 ? (
        <Error404 />
      ) : error.status === 500 ? (
        <Error500 />
      ) : error.status === 401 ? (
        <Error401 />
      ) : isRouteErrorResponse(error) ? (
        <ErrorRouting />
      ) : error instanceof Error ? (
        <ErrorDefault />
      ) : (
        <ErrorDefault title="Unknown error" />
      )}
    </Form>
  );
}
